// ** React Imports
import { useState, useEffect, useCallback, useMemo } from "react";

// ** MUI Imports
import Fab from "@mui/material/Fab";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";

// ** Icon Imports
import Icon from "../components/icon";

// ** Theme Config Import
import themeConfig from "../components/configs/themeConfig";

// ** Components
import AppBar from "./components/vertical/appBar";
import Navigation from "./components/vertical/navigation";
import Footer from "./components/shared-components/footer";
import ScrollToTop from "../components/scroll-to-top";

const MainContentWrapper = styled(Box)({
  flexGrow: 1,
  minWidth: 0,
  display: "flex",
  minHeight: "100vh",
  flexDirection: "column",
});

const ContentWrapper = styled("main")(({ theme }) => ({
  flexGrow: 1,
  width: "100%",
  padding: theme.spacing(6),
  transition: "padding .25s ease-in-out",
  [theme.breakpoints.down("sm")]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

const VerticalLayoutWrapper = styled("div")({
  height: "100%",
  display: "flex",
  // background: 'linear-gradient(to bottom right, #daf0ff, #e3ebf2, #cbf5dd)'
  background: "var(--layout-bg)",
});

const setLayoutBackground = (mode) => {
  document.documentElement.style.setProperty(
    "--layout-bg",
    mode === "dark"
      ? "#0c0c0c"
      : "linear-gradient(to bottom right, #daf0ff, #e3ebf2, #cbf5dd)"
  );
};

const VerticalLayout = (props) => {
  // ** Props
  const {
    hidden,
    settings,
    children,
    scrollToTop,
    footerProps,
    contentHeightFixed,
    verticalLayoutProps,
    activeLink,
  } = props;

  // ** Vars
  const { skin, navHidden, contentWidth } = settings;
  const navigationBorderWidth = skin === "bordered" ? 1 : 0;
  const { navigationSize, disableCustomizer, collapsedNavigationSize } =
    themeConfig;
  const navWidth = navigationSize;
  const collapsedNavWidth = collapsedNavigationSize;

  // ** States
  const [navVisible, setNavVisible] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(settings.navCollapsed);

  const toggleNavVisibility = () => {
    setNavCollapsed((prev) => { 
      settings.navCollapsed = !prev;
      return !prev;
    });
  }

  useEffect(() => {
    setLayoutBackground(settings.mode);
  }, [settings.mode]);

  const navigationComponent = useMemo(() => {
    if (navHidden && !(navHidden && settings.lastLayout === "horizontal")) {
      return null;
    }

    return (
      <Navigation
        navWidth={navWidth}
        navVisible={navVisible}
        activeLink={activeLink}
        setNavVisible={setNavVisible}
        collapsedNavWidth={collapsedNavWidth}
        toggleNavVisibility={toggleNavVisibility}
        navigationBorderWidth={navigationBorderWidth}
        navMenuContent={verticalLayoutProps.navMenu.content}
        navMenuBranding={verticalLayoutProps.navMenu.branding}
        menuLockedIcon={verticalLayoutProps.navMenu.lockedIcon}
        verticalNavItems={verticalLayoutProps.navMenu.navItems}
        navMenuProps={verticalLayoutProps.navMenu.componentProps}
        menuUnlockedIcon={verticalLayoutProps.navMenu.unlockedIcon}
        afterNavMenuContent={verticalLayoutProps.navMenu.afterContent}
        beforeNavMenuContent={verticalLayoutProps.navMenu.beforeContent}
        {...props}
      />
    );
  }, [
    navHidden,
    settings.lastLayout,
    navWidth,
    navVisible,
    activeLink,
    collapsedNavWidth,
    toggleNavVisibility,
    navigationBorderWidth,
    verticalLayoutProps,
    props,
  ]);

  // Memoize scroll to top component
  const scrollToTopComponent = useMemo(() => {
    if (scrollToTop) {
      return scrollToTop(props);
    }

    return (
      <ScrollToTop className="mui-fixed">
        <Fab color="primary" size="small" aria-label="scroll back to top">
          <Icon icon="tabler:arrow-up" />
        </Fab>
      </ScrollToTop>
    );
  }, [scrollToTop, props]);

  return (
    <>
      <VerticalLayoutWrapper className="layout-wrapper">
        {/* Navigation Menu */}
        {navigationComponent}

        <MainContentWrapper
          className="layout-content-wrapper"
          sx={{ ...(contentHeightFixed && { maxHeight: "100vh" }) }}
        >
          {/* AppBar Component */}
          <AppBar
            toggleNavVisibility={toggleNavVisibility}
            appBarContent={verticalLayoutProps.appBar?.content}
            appBarProps={verticalLayoutProps.appBar?.componentProps}
            {...props}
          />

          {/* Content */}
          <ContentWrapper
            className="layout-page-content"
            sx={{
              ...(contentHeightFixed && {
                overflow: "hidden",
                "& > :first-of-type": { height: "100%" },
              }),
              ...(contentWidth === "boxed" && {
                mx: "auto",
                "@media (min-width:1200px)": { maxWidth: "100%" },
              }),
            }}
          >
            {children}
          </ContentWrapper>

          {/* Footer Component */}
          <Footer
            footerStyles={footerProps?.sx}
            footerContent={footerProps?.content}
            {...props}
          />
        </MainContentWrapper>
      </VerticalLayoutWrapper>

      {/* Scroll to top button */}
      {scrollToTopComponent}
    </>
  );
};

export default VerticalLayout;